import {
  CreateOrderFormModel,
  isHeadlineTime,
  useCreateBasketOrderQuery,
  useCreateConditionalOrderQuery,
  useCreateOfflineOrder,
  useCreateOrderQuery,
  useGetServerClock,
  useUpdateOrderQuery,
} from '@core/order';
import { sleep } from '@libs/utils';
import { UseMutationConfig, useQueryClient } from '@libs/use';
import { dataLayer } from '@libs/gtm';
import { useI18n } from 'vue-i18n';
import { useToast, useDialog } from '@negar/core';
import { OrderItem } from '@libs/api-reference';
import { computed, ref } from 'vue';
import { OFFLINE_ORDER_AGREEMENT_ID, useUserAgreements } from '@core/user';
import { Fn } from '@vueuse/core';

import { useUserDialogs } from '@/modules/user';
import {
  HELIUM_DEVICE_TYPE,
  ORDER_API_ERROR_DESCRIPTION,
  useOrderDialogs,
  ORDER_EXECUTION_TYPE,
  BASKET_ORDER_EXECUTION_TYPE,
  CONDITIONAL_ORDER_EXECUTION_TYPE,
} from '@/modules/order';

export type TCreateMarketOrderArguments = {
  shouldCloseDialog: boolean;
  resetFormModel?: Fn;
  formModel: {
    quantity: number;
    price: number;
    totalValue: number;
    orderExecutionType: string;
  };
  isMarketOrderBasedOnQuantity: boolean;
};

export type TCreateOfflineOrderArguments = {
  shouldCloseDialog: boolean;
  resetFormModel?: Fn;
  formModel: {
    quantity?: number | null;
    price?: number | null;
    totalValue?: number | null;
    executedQuantity: number | null;
    validityDate: Date;
    orderExecutionType: string;
  };
};

export default function useCreateOrder({
  isin,
  orderType,
  selectedOrder,
  onSuccess,
  onReject,
}: {
  isin: string;
  orderType: 'buy' | 'sell';
  selectedOrder?: OrderItem;

  onReject?: UseMutationConfig<any>['onReject'];
  onSuccess?: UseMutationConfig<any>['onSuccess'];
}) {
  const { t } = useI18n();
  const toast = useToast();
  const { remove: removeDialog } = useDialog();
  const queryClient = useQueryClient();

  const { openApproveAlgorithmAgreementDialog } = useUserDialogs();
  const { openOfflineOrderSubmissionDialog } = useOrderDialogs();

  const { userAgreements, isLoadingAgreements } = useUserAgreements();

  const { time, isErrorLoadingServerClock } = useGetServerClock();

  const submittedCreateOrderId = ref<string>();

  const offlineOrderAgreement = computed(() =>
    userAgreements.value?.find(
      (agreement) => agreement.agreementId === OFFLINE_ORDER_AGREEMENT_ID
    )
  );

  const { createOfflineOrder, createMarketOrder, isLoadingCreateOfflineOrder } =
    useCreateOfflineOrder({
      symbol: isin,
      type: orderType,
      async onSuccess(_, { shouldCloseDialog }) {
        if (shouldCloseDialog) {
          removeDialog();
        }

        toast.neutral({
          message: t('modules.order.create_order_message'),
          duration: 3000,
        });

        await sleep(1000);

        await queryClient.invalidateQueries(['orders']);

        dataLayer.push({
          event: 'offlineOrderSent',
          orderSentStatus: 'Succeed',
          orderSentDescription: null,
        });

        onSuccess?.();
      },
      onReject(error) {
        const messages = error?.response?.data?.map(
          ({ message }) => message
        ) || [t('orders.errorCreatingOrderMessage')];

        for (const message of messages) {
          toast.negative({
            message,
            duration: 3000,
          });
        }

        dataLayer.push({
          event: 'offlineOrderSent',
          orderSentStatus: 'Failed',
          orderSentDescription: messages.join(','),
        });

        onReject?.();
      },
    });

  const { createConditionalOrder, isLoadingCreateConditionalOrder } =
    useCreateConditionalOrderQuery({
      symbol: isin,
      type: orderType,
      orderExecutionType: CONDITIONAL_ORDER_EXECUTION_TYPE,
      async onSuccess(_, { shouldCloseDialog }) {
        if (shouldCloseDialog) {
          removeDialog();
        }

        toast.neutral({
          message: t('modules.order.create_order_message'),
          duration: 3000,
        });

        await sleep(1000);

        await queryClient.invalidateQueries(['orders']);

        dataLayer.push({
          event: 'conditionalOrderSent',
          orderSentStatus: 'Succeed',
          orderSentDescription: null,
        });

        onSuccess?.();
      },
      onReject(error) {
        const messages = error?.response?.data?.map(
          ({ message }) => message
        ) || [t('orders.errorCreatingOrderMessage')];

        for (const message of messages) {
          toast.negative({
            message,
            duration: 3000,
          });
        }

        dataLayer.push({
          event: 'conditionalOrderSent',
          orderSentStatus: 'Failed',
          orderSentDescription: messages.join(','),
        });

        onReject?.();
      },
    });

  const { createOrder, isLoadingCreateOrder } = useCreateOrderQuery({
    orderExecutionType: ORDER_EXECUTION_TYPE,
    deviceType: HELIUM_DEVICE_TYPE,
    symbol: isin,
    type: orderType,
    onSuccess(response, { shouldCloseDialog }) {
      if (shouldCloseDialog) {
        removeDialog();
      }

      submittedCreateOrderId.value = response.data?.id;

      toast.neutral({
        message: t('modules.order.create_order_message'),
        duration: 3000,
      });

      dataLayer.push({
        event: 'orderSent',
        orderSentStatus: 'Succeed',
        orderSentDescription: null,
      });

      onSuccess?.();
    },
    onReject(error, { formModel }: { formModel: CreateOrderFormModel }) {
      const messages = error?.response?.data?.map(({ message }) => message) || [
        t('orders.errorCreatingOrderMessage'),
      ];

      if (
        !isErrorLoadingServerClock.value &&
        !isHeadlineTime(time.value) &&
        ORDER_API_ERROR_DESCRIPTION.some((msg) =>
          messages.some((message) => message === msg)
        )
      ) {
        openOfflineOrderSubmissionDialog({
          formModel,
          isin,
          type: orderType,
        });
      } else {
        for (const message of messages) {
          toast.negative({
            message,
            duration: 3000,
          });
        }
      }

      dataLayer.push({
        event: 'orderSent',
        orderSentStatus: 'Failed',
        orderSentDescription: messages.join(','),
      });
      onReject?.();
    },
  });
  const { updateOrder, isLoadingUpdateOrder } = useUpdateOrderQuery({
    order: selectedOrder,
    orderExecutionType: ORDER_EXECUTION_TYPE,
    deviceType: HELIUM_DEVICE_TYPE,
    onSuccess(_, { shouldCloseDialog }) {
      if (shouldCloseDialog) {
        removeDialog();
      }

      toast.neutral({
        message: t('modules.order.editOrderMessage'),
        duration: 3000,
      });

      dataLayer.push({
        event: 'orderEdit',
        orderSentStatus: 'Succeed',
        orderSentDescription: null,
      });

      onSuccess?.();
    },
    onReject(error) {
      const messages = error?.response?.data?.map(({ message }) => message) || [
        t('orders.errorCreatingOrderMessage'),
      ];

      for (const message of messages) {
        toast.negative({
          message,
          duration: 3000,
        });
      }

      dataLayer.push({
        event: 'orderEdit',
        orderSentStatus: 'Failed',
        orderSentDescription: messages.join(','),
      });

      onReject?.();
    },
  });
  const { createBasketOrder, isLoadingCreateBasketOrder } =
    useCreateBasketOrderQuery({
      symbol: isin,
      type: orderType,
      deviceType: HELIUM_DEVICE_TYPE,
      orderExecutionType: BASKET_ORDER_EXECUTION_TYPE,
      onSuccess(_, { shouldCloseDialog }) {
        if (shouldCloseDialog) {
          removeDialog();
        }

        toast.neutral({
          message: t('orders.createOrderMessage'),
          duration: 3000,
        });

        onSuccess?.();
      },
      onReject(error) {
        if (error) {
          for (const { message } of error.response.data) {
            toast.negative({
              message,
              duration: 3000,
            });
          }
        } else {
          toast.negative({
            message: t('orders.errorCreatingOrderMessage'),
            duration: 3000,
          });
        }

        onReject?.();
      },
    });

  const submitConditionalOrder = (args: {
    shouldCloseDialog?: boolean;
    resetFormModel?: Fn;
    formModel: {
      quantity?: number | null;
      price?: number | null;
      upperThreshold?: number | null;
      lowerThreshold?: number | null;
      totalValue?: number | null;
      executedQuantity: number | null;
      validityDate: number;
      priceCondition: { value: string; label: string };
      bypassSellQuantity: boolean;
    };
  }) => {
    if (!offlineOrderAgreement.value?.isApproved) {
      openApproveAlgorithmAgreementDialog({
        agreement: offlineOrderAgreement,
        onConfirmCallback: () => createConditionalOrder(args),
      });

      return;
    }

    createConditionalOrder(args);
  };

  const submitOfflineOrder = (args: TCreateOfflineOrderArguments) => {
    if (!offlineOrderAgreement.value?.isApproved) {
      openApproveAlgorithmAgreementDialog({
        agreement: offlineOrderAgreement,
        onConfirmCallback: () => createOfflineOrder(args),
      });

      return;
    }

    createOfflineOrder(args);
  };

  const submitMarketOrder = (args: TCreateMarketOrderArguments) => {
    if (!offlineOrderAgreement.value?.isApproved) {
      openApproveAlgorithmAgreementDialog({
        agreement: offlineOrderAgreement,
        onConfirmCallback: () => createOfflineOrder(args),
      });

      return;
    }

    createMarketOrder(args);
  };

  return {
    createOrder,
    updateOrder,
    createBasketOrder,

    createOfflineOrder: submitOfflineOrder,
    createMarketOrder: submitMarketOrder,
    createConditionalOrder: submitConditionalOrder,

    submittedCreateOrderId,

    isLoadingCreateOrder,
    isLoadingCreateBasketOrder,
    isLoadingUpdateOrder,

    isLoadingCreateConditionalOrder,
    isLoadingCreateOfflineOrder,
    isLoadingAgreements,
  };
}
